import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { squidloansUserAPI } from '../../services/api';
import FlowFooter from '../../components/FlowFooter';
import useLeadStage from '../../hooks/useLeadStage';
import { automationNodeLeadStageToRouteMap, LEAD_STAGE } from '../../Utils/constants';

const curr_page_lead_stage = [
  LEAD_STAGE.ADD_EMPLOYEMENT,
];

function Email() {
  const navigate = useNavigate();
  const { leadStage, isLoadingStage } = useLeadStage();
  const [formData, setFormData] = useState({
    personalEmail: '',
    workEmail: '',
  });
  const [errors, setErrors] = useState({
    personalEmail: '',
    workEmail: '',
    submit: '',
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const sanitized = value.replace(/[^a-zA-Z0-9@._-]/g, '');
    setFormData((prev) => ({ ...prev, [name]: sanitized }));
    setErrors((prev) => ({ ...prev, [name]: '', submit: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.personalEmail) newErrors.personalEmail = 'Personal email is required';
    if (!formData.workEmail) newErrors.workEmail = 'Office email is required';
    if (
      formData.personalEmail &&
      formData.workEmail &&
      formData.personalEmail.toLowerCase() === formData.workEmail.toLowerCase()
    ) {
      newErrors.workEmail = 'Personal and office emails cannot be the same';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors((prev) => ({ ...prev, submit: '' }));
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      await squidloansUserAPI.verifyEmail({
        office_email: formData.workEmail,
        personal_email: formData.personalEmail,
      });
      navigate('/apply/account-aggregator');
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        submit:
          error.message ||
          error.response?.data?.message ||
          'Failed to verify email. Please try again.',
      }));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isLoadingStage) {
      return;
    }

    if (leadStage && !curr_page_lead_stage.includes(leadStage)) {
      navigate(automationNodeLeadStageToRouteMap[leadStage]);
    }
  }, [leadStage, navigate, isLoadingStage]);
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#FFFFFF] to-[#F1F5F9] text-[#1E293B] relative flex flex-col">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="hidden sm:block absolute -top-20 -right-10 w-80 h-80 bg-[#FF6B35]/10 blur-[120px]"></div>
        <div className="hidden sm:block absolute top-32 -left-10 w-72 h-72 bg-[#1E3A8A]/10 blur-[110px]"></div>
        <div className="hidden sm:block absolute bottom-0 right-1/3 w-96 h-96 bg-[#FF6B35]/5 blur-[130px]"></div>
      </div>

      <div className="relative flex-1 flex flex-col pt-20 sm:pt-24 md:pt-28 pb-6 sm:pb-10 px-3 sm:px-4 md:px-6 lg:px-10 mt-0 overflow-y-auto">
        <div className="max-w-2xl w-full mx-auto space-y-4 sm:space-y-6 py-2 sm:py-4">
          <div className="text-center space-y-2 sm:space-y-3 mb-2 sm:mb-4 px-2">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold leading-tight text-[#1E3A8A]">
              Enter your <span className="text-[#FF6B35]">email addresses</span>
            </h1>
            <p className="text-sm sm:text-base text-[#64748B]">
              Provide both your personal and official work email
            </p>
          </div>

          <div className="bg-white border border-[#E2E8F0] rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 shadow-2xl shadow-[#FF6B35]/10">
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              <div>
                <label className="text-xs sm:text-sm font-semibold tracking-wide text-[#1E293B] mb-1 block">
                  Personal Email <span className="text-[#FF6B35]">*</span>
                </label>
                <input
                  type="email"
                  name="personalEmail"
                  value={formData.personalEmail}
                  onChange={handleInputChange}
                  placeholder="Personal Email"
                  className={`w-full px-3 sm:px-4 py-2.5 sm:py-3.5 rounded-xl sm:rounded-2xl bg-white border text-sm sm:text-base ${
                    errors.personalEmail ? 'border-red-500' : 'border-[#E2E8F0] focus:border-[#FF6B35]'
                  } text-[#1E293B] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/40`}
                />
                {errors.personalEmail && (
                  <p className="text-xs text-red-500 mt-1 sm:mt-2">{errors.personalEmail}</p>
                )}
              </div>

              <div>
                <label className="text-xs sm:text-sm font-semibold tracking-wide text-[#1E293B] mb-1 block">
                  Office Email <span className="text-[#FF6B35]">*</span>
                </label>
                <input
                  type="email"
                  name="workEmail"
                  value={formData.workEmail}
                  onChange={handleInputChange}
                  placeholder="Office Email"
                  className={`w-full px-3 sm:px-4 py-2.5 sm:py-3.5 rounded-xl sm:rounded-2xl bg-white border text-sm sm:text-base ${
                    errors.workEmail ? 'border-red-500' : 'border-[#E2E8F0] focus:border-[#FF6B35]'
                  } text-[#1E293B] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/40`}
                />
                {errors.workEmail && <p className="text-xs text-red-500 mt-1 sm:mt-2">{errors.workEmail}</p>}
              </div>

              {errors.submit && (
                <div className="bg-red-50 border border-red-200 rounded-xl sm:rounded-2xl p-3 sm:p-4 text-xs sm:text-sm text-red-700">
                  {errors.submit}
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading || !formData.personalEmail || !formData.workEmail}
                className="w-full py-3 sm:py-3.5 rounded-xl sm:rounded-2xl bg-gradient-to-r from-[#FF6B35] to-[#ff8c42] text-white text-sm sm:text-base font-semibold hover:opacity-90 transition-all disabled:opacity-70 disabled:cursor-not-allowed shadow-md hover:shadow-lg flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
      <FlowFooter />
    </div>
  );
}

export default Email;
